import * as React from 'react';
import MenuItem from '@mui/material/MenuItem';
import useGlynt from '../../../hooks/useGlynt';
import { AppContext } from '../../../../App';
import { IDocument } from '../../../../types/my-types';
import { ToolActionsContext } from '../../../../context/ToolActionsContext';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function DownloadDocumentsToolNav() {
  const { setToast } = React.useContext(AppContext);
  const { isReview, setOpen } = React.useContext(ToolActionsContext);
  const { did, eid } = useParams<{ did: string; eid: string }>();
  const docId = isReview ? eid : did;

  // TODO: refactor to hook
  const glynt = useGlynt();
  const client = useQueryClient();
  const { refetch } = useQuery({
    queryKey: ['doc'],
    queryFn: async () => (await glynt).document.retrieve(docId!),
    enabled: false
  });
  const { refetch: refetchDocs } = useQuery({
    queryKey: ['docsByExtraction'],
    queryFn: async () => (await glynt).document.list({ extraction: docId }),
    enabled: false
  });

  // S3 presigned-URL fetches occasionally fail with `TypeError: Failed to fetch`
  // (connection-level: dropped TCP, CORS preflight timeout, transient DNS). One
  // retry usually clears it, so try up to 3 times with backoff before surfacing
  // an error to the user.
  const fetchWithRetry = async (
    url: string,
    init: RequestInit,
    attempts = 3,
    backoffMs = 500
  ): Promise<Response> => {
    let lastError: unknown;
    for (let i = 0; i < attempts; i++) {
      try {
        const response = await fetch(url, init);
        if (response.status === 200) return response;
        lastError = new Error(`HTTP ${response.status}`);
      } catch (err) {
        lastError = err;
      }
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, backoffMs * 2 ** i));
      }
    }
    throw lastError;
  };

  const download = (doc: IDocument) => {
    client.fetchQuery({
        queryKey: ['docFile'],
        queryFn: async () => (await glynt).document.getFile(doc.id),
      })
      .then((data) => {
        const fileURL = data.file_temp_url;

        fetchWithRetry(fileURL, {
          method: 'get',
          headers: new Headers({
            accept: doc.content_type,
          }),
        })
          .then((response) => response.blob())
          .then((blob) => {
            saveAs(blob, `${doc.label}.${doc.content_type.split('/')[1]}`);
          })
          .catch(() => {
            setToast({
              open: true,
              message: 'Failed to download document',
              variant: 'error',
            });
          });
      })
      .catch(() => {
        setToast({
          open: true,
          message: 'Failed to download document',
          variant: 'error',
        });
      });
  };

  const downloadDoc = () => {
    setToast({
      open: true,
      message: 'Downloading...',
      variant: 'success',
    });
    if (isReview) {
      refetchDocs()
        .then((data) => {
          // console.log(data);
          let doc = data.data?.results[0];
          if (doc) {
            download(doc);
            setToast({ open: false });
          }
        })
        .catch((error) => {
          console.log(error);
          setToast({
            open: true,
            message: 'Failed to download document',
            variant: 'error',
          });
        });
    } else {
      refetch()
        .then((data) => {
          let doc = data.data;
          if (doc) {
            download(doc);
            setToast({ open: false });
          }
        })
        .catch(() => {
          setToast({
            open: true,
            message: 'Failed to download document',
            variant: 'error',
          });
        });
    }
    setOpen(false);
  };
  return (
    <MenuItem onClick={() => downloadDoc()} data-cy="btn-tool-nav-download-doc">
      Download document
    </MenuItem>
  );
}

export default DownloadDocumentsToolNav;
